/**
 * Handles Aadhaar verification engine change detection and recovery mechanism
 *
 * This utility function is designed to handle breaking changes in the Aadhaar verification API.
 * When the backend detects that the client is using an outdated API version or configuration,
 * it responds with a specific HTTP status code (405) to signal that
 * the verification engine has been updated and requires a client-side refresh.
 *
 * The function implements an automatic recovery strategy by forcing a full page reload,
 * which ensures that:
 * - The latest client-side configuration is fetched
 * - Updated API endpoints and parameters are loaded
 * - Any cached data or state that might be incompatible is cleared
 * - The user experience remains seamless during engine transitions
 *
 * @param status - HTTP status code from the Aadhaar verification API response
 *                 Expected values:
 *                 - 405: Engine change detected, triggers page reload
 *                 - Any other code: Normal processing continues
 *
 * @param fallback - Optional callback function to execute when no engine change is detected
 *                   This allows for custom error handling or alternative processing logic
 *                   when the status code is not 405.
 */
export const handleAadhaarVerificationEngineChange = (status: number, fallback?: () => void) => {
  const engineAPIChangedStatusCode = 405;

  if (status === engineAPIChangedStatusCode) {
    // Force a full page reload so the client picks up the latest config
    window.location.reload();
  } else {
    fallback?.();
  }
};
